/**
 * GuidedServiceGenerationView — Service Generation stage (Modernization Journey)
 *
 * Receives the approved decomposition job ID from Decompose Code and runs
 * Service Generation for the current project.
 *
 * On mount:
 *   - Discovers code_analysis_job_id from jobs API (same pattern as DecomposeCodeView)
 *   - Loads the approved contract summary from /decomposeCode/{approvedJobId}/contract
 *
 * Run flow:
 *   - POST /servicegeneration via serviceGenerationExecutor
 *   - Poll until complete — show live progress
 *   - Show result summary when done
 */

import { useState, useEffect, useRef } from 'react'
import {
  ArrowLeft, ChevronRight, Settings, HelpCircle, User, Home,
  Workflow, Plug, FileBarChart, BarChart2, LayoutGrid,
  ChevronDown, CheckCircle2, Loader2, AlertTriangle, AlertCircle,
  Zap, Package, Layers, Lock, FileCode, Share2,
} from 'lucide-react'
import { getAPIBaseUrl, isQA } from '../../environment'
import './GuidedServiceGenerationView.css'
import '../runs/RunsView.css'

export default function GuidedServiceGenerationView({
  project,
  approvedJobId,        // decompose_code job ID carrying the approved contract (may be null)
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToDecomposeCode,
  onGoToGenerateDocs,   // navigate to Generate Documentation
  onGoToExtractBizLogic, // navigate to Extract Business Logic (receives caJobId)
  onGoToJavaPkg,        // Journey handoff: called with job ids array when user continues to Java Packaging
  onGoToRefineCode,     // Navigate to Refine Code stage
  onGoToRefactorCode,   // Navigate to Refactor Code stage
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
}) {
  const projectName = project?.name || 'Unknown Project'
  const accountId   = project?.account || ''
  const apiBase     = getAPIBaseUrl()

  // ── Discovery state ───────────────────────────────────────────────────────
  const [discovering,          setDiscovering]          = useState(true)
  const [caJobId,              setCaJobId]              = useState(null)
  const [contractSummary,      setContractSummary]      = useState(null)   // { serviceCount, services, isApproved }
  const [discoverError,        setDiscoverError]        = useState(null)
  const [previousSvcGenJobIds, setPreviousSvcGenJobIds] = useState([])     // discovered prior svcgen job IDs (previous-run path)
  const [dsJobId,              setDsJobId]              = useState(null)    // define_services job ID (for handoff plan)

  // ── Run state ─────────────────────────────────────────────────────────────
  const [running,     setRunning]    = useState(false)
  const [progress,    setProgress]   = useState(0)
  const [progressMsg, setProgressMsg] = useState('')
  const [runError,    setRunError]   = useState(null)
  const [result,      setResult]     = useState(null)   // completed result object

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const abortRef                       = useRef(null)

  // ── Resolve Build / Package job IDs (current result first, then discovered) ─
  const getJobIdsForPkg = () => {
    if (result) {
      // Prefer the service_job_map (deduped per service) → values are job IDs
      if (result.jobs?.service_job_map) return Object.values(result.jobs.service_job_map)
      if (result.jobs?.service_generation_job_ids?.length) return result.jobs.service_generation_job_ids
      if (result.jobs?.service_generation_job_id) return [result.jobs.service_generation_job_id]
    }
    // Fall back to previously-discovered svcgen jobs (previous-run path)
    return previousSvcGenJobIds
  }

  // ── Discover upstream jobs and load contract on mount ─────────────────────
  useEffect(() => {
    if (!accountId || !projectName) return

    const token   = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    const acctEnc = encodeURIComponent(accountId)
    const appEnc  = encodeURIComponent(projectName)

    const discover = async () => {
      setDiscovering(true)
      setDiscoverError(null)
      try {
        // 1. Find code_analysis, define_services, and prior svcgen jobs
        let discoveredDsJobId = approvedJobId || null
        for (const statusVal of ['SUCCEEDED', 'completed']) {
          try {
            const r = await fetch(
              `${apiBase}/jobs/by-app/${acctEnc}/${appEnc}?status=${statusVal}`,
              { headers },
            )
            if (!r.ok) continue
            const data = await r.json()
            const jobs = data.jobs || []
            const jt = j => j.job_type || j.flow_type || ''
            const caJob = jobs.find(
              j => jt(j) === 'code_analysis' || jt(j) === 'code-analysis',
            )
            if (caJob) { setCaJobId(caJob.job_id) }
            // Discover define_services job if not passed as prop
            if (!discoveredDsJobId) {
              const dsJob = jobs.find(j => jt(j) === 'define_services')
              if (dsJob) { discoveredDsJobId = dsJob.job_id }
            }
            // Discover prior svcgen job IDs for previous-run Build / Package path
            const svcgenJobs = jobs.filter(
              j => jt(j) === 'service_generation' || jt(j) === 'service-generation',
            )
            if (svcgenJobs.length > 0) {
              setPreviousSvcGenJobIds(svcgenJobs.map(j => j.job_id))
            }
            if (caJob) break  // stop once we have the ca job (svcgen may exist on first status pass)
          } catch (_) {}
        }
        // Store define_services job ID in state for handleRun
        if (discoveredDsJobId) { setDsJobId(discoveredDsJobId) }

        // 2. Load approved contract summary
        //    Try define-services endpoint first (v3), fall back to decomposeCode (v2)
        let contractLoaded = false

        // 2a. Try define-services (graph-powered proposals flow)
        if (discoveredDsJobId) {
          try {
            const dsRes = await fetch(
              `${apiBase}/define-services/proposals/${discoveredDsJobId}`,
              { headers },
            )
            if (dsRes.ok) {
              const dsData = await dsRes.json()
              if (dsData.locked) {
                const svcs = (dsData.services || []).map(s => ({
                  id: `svc-graph-${s.service_id}`,
                  name: s.service_name,
                  status: s.confirmed ? 'approved' : 'proposed',
                  cobol_origins: s.programs || [],
                  members: (s.paragraphs || []).map(p => ({
                    name: p, cobol: (s.programs || [])[0] || p,
                  })),
                }))
                setContractSummary({
                  serviceCount: svcs.length,
                  services:     svcs,
                  isApproved:   true,
                  disconnected: [],
                })
                contractLoaded = true
              }
            }
          } catch (_) {}
        }

        // 2b. Fall back to legacy decomposeCode contract endpoint
        if (!contractLoaded && approvedJobId) {
          try {
            const cr = await fetch(
              `${apiBase}/decomposeCode/${approvedJobId}/contract`,
              { headers },
            )
            if (cr.ok) {
              const contract = await cr.json()
              setContractSummary({
                serviceCount: contract.services?.length || 0,
                services:     contract.services || [],
                isApproved:   !!contract.is_approved,
                disconnected: contract.disconnected || [],
              })
            }
          } catch (_) {}
        }
      } catch (e) {
        setDiscoverError(e.message || 'Failed to load project context')
      } finally {
        setDiscovering(false)
      }
    }

    discover()
  }, [accountId, projectName, approvedJobId, apiBase])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run Service Generation ────────────────────────────────────────────────
  const handleRun = async () => {
    if (running) return

    let userId = ''
    try {
      userId = JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || ''
    } catch (_) {}

    const context = {
      api_base_url:     apiBase,
      scout_account_id: accountId,
      application_name: projectName,
      user_id:          userId,
      jobs: {
        code_analysis_job_id: caJobId || '',
      },
    }
    const config = {
      code_analysis_job_id:     caJobId || '',
      define_services_job_id:   dsJobId || '',
      base_package:             'com.modernizeit.generated',
      include_all_repositories: true,
      include_all_fields:       true,
      strict_validation:        true,
    }

    const controller = new AbortController()
    abortRef.current  = controller

    setRunning(true)
    setRunError(null)
    setProgress(0)
    setProgressMsg('Starting…')

    try {
      const { execute } = await import('../../services/nodeExecutors/serviceGenerationExecutor')
      const r = await execute(
        config,
        context,
        (pct, msg) => { setProgress(pct); setProgressMsg(msg) },
        controller.signal,
      )
      setResult(r)
      setProgress(100)
      setProgressMsg('Service Generation complete')
    } catch (err) {
      if (err.message !== 'Execution cancelled') {
        setRunError(err.message || 'Service Generation failed')
      }
    } finally {
      setRunning(false)
      abortRef.current = null
    }
  }

  // ── Download generated services ZIP ──────────────────────────────────────
  // Single-job download (kept for fallback / non-fan-out runs)
  const handleDownloadServices = async (jobId, label) => {
    const token = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    try {
      const res = await fetch(`${apiBase}/servicegeneration/${encodeURIComponent(jobId)}/download`, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = label ? `${label}_services.zip` : `${jobId}_services.zip`
      document.body.appendChild(a)
      a.click()
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 500)
    } catch (err) {
      console.error('[SvcGen] Download failed:', err.message)
    }
  }

  // Combined download — fan-out runs: merges all per-service S3 artifacts into ONE ZIP
  const [isCombinedDownloading, setIsCombinedDownloading] = useState(false)
  const handleDownloadCombined = async (jobIds, appLabel) => {
    if (!jobIds?.length) return
    const token = localStorage.getItem('modernizeit_token') || ''
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    setIsCombinedDownloading(true)
    try {
      const res = await fetch(`${apiBase}/servicegeneration/download-combined`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ job_ids: jobIds, app_name: appLabel || projectName || 'application' }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `${appLabel || projectName || 'application'}_services.zip`
      document.body.appendChild(a)
      a.click()
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 500)
    } catch (err) {
      console.error('[SvcGen] Combined download failed:', err.message)
      alert(`Download failed: ${err.message}`)
    } finally {
      setIsCombinedDownloading(false)
    }
  }

  const getShortName = () => {
    const name = userProfile?.displayName || userProfile?.email || ''
    return name.split(' ')[0] || name || 'User'
  }

  // ── Nav header ────────────────────────────────────────────────────────────
  const renderNavHeader = () => (
    <div className="runs-header-bar">
      <div className="runs-header-left">
        <button className="runs-back-btn" onClick={onBack}>
          <ArrowLeft size={15} /><span>Runs</span>
        </button>
        <div className="runs-breadcrumb">
          <span className="runs-bc-project">{projectName}</span>
          <ChevronRight size={14} className="runs-bc-sep" />
          <span className="runs-bc-page">Build Services</span>
        </div>
      </div>

      <div className="runs-header-center">
        <div className="mode-switcher">
          <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
            <Home size={16} /><span>Home</span>
          </button>
          <button className="mode-btn" onClick={() => onGoToAnalyze?.()} title="Analyze">
            <BarChart2 size={16} /><span>Analyze</span>
          </button>
          {/* Workflow and MCP hidden — accessible via Journey dropdown
          <button className="mode-btn" disabled title="Workflow">
            <Workflow size={16} /><span>Workflow</span>
          </button>
          <button className="mode-btn" disabled title="MCP — coming soon">
            <Plug size={16} /><span>MCP</span>
          </button>
          */}
          <button className="mode-btn" onClick={() => onOpenReports?.()} title="Reports">
            <FileBarChart size={16} /><span>Reports</span>
          </button>

          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              disabled={false}
              title="HITL Journey"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToGenerateDocs?.() }}>
                    <span>Generate Documentation</span>
                  </button>
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToExtractBizLogic?.(caJobId) }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToDecomposeCode?.() }}>
                    <span>Define Services</span>
                  </button>
                  <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                    <span>Build Services</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}><span>Refactor Code</span></button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                    <span>Refine Code</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.(getJobIdsForPkg()) }}>
                    <span>Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="runs-header-right">
        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help">
          <HelpCircle size={18} />
        </button>
        <div className="user-menu-container">
          <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
            <div className="user-avatar"><User size={16} /></div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>
          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || ''}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                <Settings size={16} /><span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack?.() }}>
                <Home size={16} /><span>Back to Runs</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ── Stage header ──────────────────────────────────────────────────────────
  const renderStageHeader = () => (
    <div className="gsv-stage-header">
      <div className="gsv-stage-left">
        <div className="gsv-stage-title-row">
          <h2 className="gsv-stage-title">Build Services</h2>
          <span className="gsv-stage-set-name">{projectName} · Modernization Journey</span>
        </div>
        <div className="gsv-stage-chips">
          <span className="gsv-chip gsv-chip-done">Analyze ✓</span>
          <span className="gsv-chip-sep">›</span>
          <span className="gsv-chip gsv-chip-done">Define Services ✓</span>
          <span className="gsv-chip-sep">›</span>
          <span className="gsv-chip gsv-chip-active">Build Services</span>
        </div>
      </div>
      <div className="gsv-stage-right">
        {result ? (
          <span className="gsv-badge gsv-badge-success"><CheckCircle2 size={11} />Complete</span>
        ) : running ? (
          <span className="gsv-badge gsv-badge-running"><Loader2 size={11} className="gsv-spin" />Running</span>
        ) : (
          <span className="gsv-badge gsv-badge-ready"><Package size={11} />Ready to Build</span>
        )}
      </div>
    </div>
  )

  // ── Main content ──────────────────────────────────────────────────────────
  const renderContent = () => {

    if (discovering) {
      return (
        <div className="gsv-loading">
          <Loader2 size={20} className="gsv-spin" />
          <span>Loading project context…</span>
        </div>
      )
    }

    // ── COMPLETED STATE ────────────────────────────────────────────────────
    if (result) {
      // ── Derived data (null-safe throughout) ──────────────────────────────
      // The service_generation_executor emits summary.service_count (not total_services).
      // Read both so this works whether the backend field name is ever updated.
      const totalServices    = result.summary?.total_services ?? result.summary?.service_count ?? contractSummary?.serviceCount ?? 0
      const totalMethods     = result.summary?.total_methods ?? result.summary?.method_count ?? null
      const validationPassed = result.summary?.validation_passed ?? result.validation?.passed ?? null
      const validationChecks = result.validation?.results        || result.validation?.checks    || []
      const resultServices   = result.services?.length > 0       ? result.services               : null
      const durationSec      = result.duration_ms                ? `${(result.duration_ms / 1000).toFixed(1)}s` : null
      // When the executor doesn't return a services list (worker-mode path), fall back
      // to the contract — but filter to services that actually had COBOL programs
      // assigned.  Only cobolOrigins / cobol_origins are the real COBOL origin fields;
      // members is a decompose-domain structure and must NOT be used here.
      // Services with zero COBOL origins were never passed to the generator.
      const displayServices  = resultServices
        || (contractSummary?.services
              ? contractSummary.services.filter(
                  s => (s.cobolOrigins?.length || s.cobol_origins?.length || 0) > 0
                )
              : [])
      // RC-UI-B: Count COBOL programs across actually-generated services only.
      // Computed after displayServices so it uses the filtered list, not the full contract.
      // members is intentionally excluded — it is a decompose-domain grouping structure,
      // not a COBOL program origin list; using it inflates the count with non-COBOL names.
      const programsIncluded = displayServices.reduce(
        (n, s) => n + (s.cobol_origins?.length || s.cobolOrigins?.length || 0), 0
      ) || 0
      const jobId            = result.jobs?.service_generation_job_id
      const multiJobIds      = result.jobs?.service_generation_job_ids
      // service_job_map is deduplicated: { serviceName: jobId } — one entry per distinct
      // service regardless of how many COBOL programs fed into it.  This is the ground
      // truth for download buttons.  Falls back to null for single-job runs.
      const serviceJobMap    = result.jobs?.service_job_map || null
      const serviceJobEntries = serviceJobMap ? Object.entries(serviceJobMap) : null

      return (
        <div className="gsv-result">

          {/* ── Banner ─────────────────────────────────────────────────────── */}
          <div className="gsv-result-banner">
            <CheckCircle2 size={20} className="gsv-result-icon" />
            <div className="gsv-result-banner-body">
              <span className="gsv-result-title">Build Services Complete</span>
              <span className="gsv-result-sub">
                {totalServices > 0
                  ? `${totalServices} Spring Boot service${totalServices !== 1 ? 's' : ''} generated for ${projectName}`
                  : `${projectName} — generated services are ready to review`}
                {durationSec ? ` · ${durationSec}` : ''}
              </span>
            </div>
            {jobId && (
              <code className="gsv-result-jobid-badge" title={jobId}>
                {jobId.slice(-14)}
              </code>
            )}
          </div>

          {/* ── Stat cards ─────────────────────────────────────────────────── */}
          <div className="gsv-stat-cards">
            <div className="gsv-stat-card">
              <span className="gsv-stat-card-num">{totalServices}</span>
              <span className="gsv-stat-card-lbl">Service{totalServices !== 1 ? 's' : ''} Built</span>
            </div>
            {totalMethods > 0 && (
              <div className="gsv-stat-card">
                <span className="gsv-stat-card-num">{totalMethods}</span>
                <span className="gsv-stat-card-lbl">Methods Generated</span>
              </div>
            )}
            {programsIncluded > 0 && (
              <div className="gsv-stat-card">
                <span className="gsv-stat-card-num">{programsIncluded}</span>
                <span className="gsv-stat-card-lbl">COBOL Programs Mapped</span>
              </div>
            )}
            {validationPassed !== null && (
              <div className={`gsv-stat-card ${validationPassed ? 'gsv-stat-card-pass' : 'gsv-stat-card-fail'}`}>
                <span className="gsv-stat-card-num">{validationPassed ? '✓' : '✗'}</span>
                <span className="gsv-stat-card-lbl">Validation</span>
              </div>
            )}
          </div>

          {/* ── Generated services ─────────────────────────────────────────── */}
          {displayServices.length > 0 && (
            <div className="gsv-result-section">
              <div className="gsv-result-section-hdr">
                <FileCode size={12} className="gsv-result-section-ico" />
                <span className="gsv-result-section-title">Generated Services</span>
                <span className="gsv-result-section-count">{displayServices.length}</span>
              </div>
              <div className="gsv-result-svc-table">
                {displayServices.map((svc, i) => {
                  // RC-UI-C: When result.services is empty (worker-mode), displayServices
                  // falls back to contractSummary.services whose method_count is absent and
                  // members.length is a COBOL-program count, not a Java method count.
                  // For single-service runs, use the actual summary total instead.
                  const isContractFallback = !resultServices
                  const methodCount = svc.method_count
                    ?? (isContractFallback && displayServices.length === 1 && totalMethods > 0
                        ? totalMethods
                        : null)
                    ?? svc.members?.length ?? svc.classes ?? null
                  const confidence  = svc.confidence != null ? `${Math.round(svc.confidence * 100)}%` : null
                  const srcLabel    = svc.source === 'decompose_contract' ? 'HITL Approved'
                                    : svc.source ? svc.source : null
                  return (
                    <div key={svc.name || svc.id || i} className="gsv-result-svc-row">
                      <div className="gsv-result-svc-left">
                        <FileCode size={12} className="gsv-result-svc-ico" />
                        <span className="gsv-result-svc-name">{svc.name}</span>
                        {srcLabel && <span className="gsv-result-svc-src">{srcLabel}</span>}
                      </div>
                      <div className="gsv-result-svc-right">
                        {methodCount != null && (
                          <span className="gsv-result-svc-stat">{methodCount} method{methodCount !== 1 ? 's' : ''}</span>
                        )}
                        {confidence && <span className="gsv-result-svc-conf">{confidence}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Source → Service mapping ────────────────────────────────────── */}
          {/* Use displayServices (already filtered to generated-only) so dismissed /    */}
          {/* non-generated services don't appear here.  Origins come from cobolOrigins  */}
          {/* (camelCase, UI-built) or cobol_origins (snake_case, API raw).  The members */}
          {/* fallback is intentionally removed — it contains service/domain names,      */}
          {/* not COBOL program identifiers, and was causing non-generated services to   */}
          {/* appear in the mapping with incorrect program chips.                        */}
          {displayServices.length > 0 && (
            <div className="gsv-result-section">
              <div className="gsv-result-section-hdr">
                <Share2 size={12} className="gsv-result-section-ico" />
                <span className="gsv-result-section-title">Source → Service Mapping</span>
                <span className="gsv-result-section-sub">COBOL programs included in each service</span>
              </div>
              <div className="gsv-source-map-table">
                {displayServices.map((svc, i) => {
                  const origins = svc.cobolOrigins?.length > 0
                    ? svc.cobolOrigins
                    : svc.cobol_origins?.length > 0
                      ? svc.cobol_origins
                      : []
                  if (origins.length === 0) return null
                  return (
                    <div key={svc.id || i} className="gsv-source-map-row">
                      <div className="gsv-source-map-svc">
                        <span className="gsv-source-map-svc-name">{svc.name}</span>
                        <span className="gsv-source-map-count">{origins.length} program{origins.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="gsv-source-map-programs">
                        {origins.map(prog => (
                          <span key={prog} className="gsv-source-map-chip">{prog}</span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* ── Validation ──────────────────────────────────────────────────── */}
          {(validationChecks.length > 0 || validationPassed !== null) && (
            <div className="gsv-result-section">
              <div className="gsv-result-section-hdr">
                <CheckCircle2 size={12} className={`gsv-result-section-ico ${validationPassed ? 'gsv-ico-pass' : validationPassed === false ? 'gsv-ico-fail' : ''}`} />
                <span className="gsv-result-section-title">Validation</span>
                {validationPassed !== null && (
                  <span className={`gsv-val-badge ${validationPassed ? 'gsv-val-badge-pass' : 'gsv-val-badge-fail'}`}>
                    {validationPassed ? 'All checks passed' : 'Issues found'}
                  </span>
                )}
              </div>
              {validationChecks.length > 0 && (
                <div className="gsv-val-checks">
                  {validationChecks.map((chk, i) => (
                    <div key={chk.name || i} className={`gsv-val-check-row ${chk.passed ? 'pass' : 'fail'}`}>
                      {chk.passed
                        ? <CheckCircle2 size={11} className="gsv-val-chk-ico pass" />
                        : <AlertCircle  size={11} className="gsv-val-chk-ico fail" />
                      }
                      <span className="gsv-val-chk-name">{(chk.name || `Check ${i+1}`).replace(/_/g, ' ')}</span>
                      {chk.message && <span className="gsv-val-chk-msg">{chk.message}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Generated output contents ───────────────────────────────────── */}
          <div className="gsv-result-section">
            <div className="gsv-result-section-hdr">
              <Package size={12} className="gsv-result-section-ico" />
              <span className="gsv-result-section-title">Generated Output</span>
              <span className="gsv-result-section-sub">what's inside the ZIP</span>
            </div>
            <div className="gsv-output-contents">
              <div className="gsv-output-row">
                <span className="gsv-output-type">Service classes</span>
                <span className="gsv-output-detail">
                  {displayServices.length > 0
                    ? displayServices.map(s => `${s.name}.java`).join(' · ')
                    : 'Spring @Service classes, one per approved service boundary'
                  }
                </span>
              </div>
              <div className="gsv-output-row">
                <span className="gsv-output-type">Boundary interfaces</span>
                <span className="gsv-output-detail">External COBOL program contracts — I&#123;Program&#125;Service.java per detected dependency</span>
              </div>
              <div className="gsv-output-row">
                <span className="gsv-output-type">Traceability map</span>
                <span className="gsv-output-detail">COBOL paragraph → Java method index (traceability_map.json)</span>
              </div>
              <div className="gsv-output-row">
                <span className="gsv-output-type">Validation report</span>
                <span className="gsv-output-detail">Generation quality checks — method count, syntax, imports, stubs (validation_report.json)</span>
              </div>
              {serviceJobEntries?.length > 1 && (
                <div className="gsv-output-row">
                  <span className="gsv-output-type">Services</span>
                  <span className="gsv-output-detail">{serviceJobEntries.length} services generated — combined into one ZIP on download</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Needs Attention ─────────────────────────────────────────────── */}
          {(() => {
            const unresolved   = (contractSummary?.disconnected || []).filter(d => d.resolution === null || d.resolution === undefined)
            const emptyBounds  = (contractSummary?.services     || []).filter(s =>
              !s.members?.length && !s.cobolOrigins?.length && !(s.classes > 0)
            )
            const failedChecks = validationChecks.filter(c => !c.passed)
            const hasAnything  = unresolved.length > 0 || emptyBounds.length > 0 || failedChecks.length > 0
            if (!hasAnything) return null
            return (
              <div className="gsv-result-section gsv-attn-section">
                <div className="gsv-result-section-hdr">
                  <AlertCircle size={12} className="gsv-result-section-ico gsv-ico-warn" />
                  <span className="gsv-result-section-title">Needs Attention</span>
                  <span className="gsv-attn-count">{unresolved.length + emptyBounds.length + failedChecks.length} item{(unresolved.length + emptyBounds.length + failedChecks.length) !== 1 ? 's' : ''}</span>
                </div>
                <p className="gsv-attn-desc">
                  The following items were excluded from generation or flagged during validation.
                  They don&apos;t block this result but may need follow-up.
                </p>

                {/* Unresolved god objects from decomposition */}
                {unresolved.length > 0 && (
                  <div className="gsv-attn-group">
                    <div className="gsv-attn-group-label">Unresolved COBOL programs — not assigned to any service boundary</div>
                    {unresolved.map((item, i) => (
                      <div key={item.id || i} className="gsv-attn-row">
                        <AlertCircle size={10} className="gsv-attn-row-ico" />
                        <span className="gsv-attn-row-name">{item.name || item.cobol || item.id}</span>
                        {item.cobol && item.name !== item.cobol && (
                          <span className="gsv-attn-row-sub">{item.cobol}</span>
                        )}
                        <span className="gsv-attn-row-tag">Excluded</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty service boundaries */}
                {emptyBounds.length > 0 && (
                  <div className="gsv-attn-group">
                    <div className="gsv-attn-group-label">Service boundaries with no COBOL programs mapped</div>
                    {emptyBounds.map((svc, i) => (
                      <div key={svc.id || i} className="gsv-attn-row">
                        <AlertCircle size={10} className="gsv-attn-row-ico" />
                        <span className="gsv-attn-row-name">{svc.name}</span>
                        <span className="gsv-attn-row-tag">Empty</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Failed validation checks */}
                {failedChecks.length > 0 && (
                  <div className="gsv-attn-group">
                    <div className="gsv-attn-group-label">Validation checks that did not pass</div>
                    {failedChecks.map((chk, i) => (
                      <div key={chk.name || i} className="gsv-attn-row">
                        <AlertCircle size={10} className="gsv-attn-row-ico" />
                        <span className="gsv-attn-row-name">{(chk.name || `Check ${i + 1}`).replace(/_/g, ' ')}</span>
                        {chk.message && <span className="gsv-attn-row-sub">{chk.message}</span>}
                        <span className="gsv-attn-row-tag fail">Failed</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* ── Action strip ────────────────────────────────────────────────── */}
          <div className="gsv-result-action-strip">
            {serviceJobEntries?.length > 0 ? (
              // Multi-service fan-out: single combined ZIP merges all services + shared class
              <button
                className="gsv-btn gsv-btn-download"
                disabled={isCombinedDownloading}
                onClick={() => handleDownloadCombined(serviceJobEntries.map(([, jid]) => jid), projectName)}
                title="Download all services combined into one ZIP"
              >
                <Package size={13} />
                {isCombinedDownloading ? 'Packaging…' : 'Download All Services'}
              </button>
            ) : jobId ? (
              <button
                className="gsv-btn gsv-btn-download"
                onClick={() => handleDownloadServices(jobId, projectName)}
                title="Download all generated Spring service code as ZIP"
              >
                <Package size={13} />Download Generated Services
              </button>
            ) : null}
            <button
              className="gsv-btn gsv-btn-secondary"
              onClick={handleRun}
              disabled={running}
            >
              <Zap size={13} />Re-run Build Services
            </button>
            <button
              className="gsv-btn gsv-btn-secondary"
              onClick={() => onGoToDecomposeCode?.()}
            >
              <ArrowLeft size={13} />Back to Define Services
            </button>
          </div>

        </div>
      )
    }

    // ── NO-CONTRACT BLOCKER — must finalize decomposition first ───────────
    if (!contractSummary) {
      return (
        <div className="gsv-no-contract-blocker">
          <div className="gsv-blocker-icon-wrap">
            <Lock size={28} className="gsv-blocker-icon" />
          </div>
          <div className="gsv-blocker-title">Approved Decomposition Required</div>
          <p className="gsv-blocker-body">
            Service Generation requires an approved decomposition plan. No finalized contract was found
            for this project. Go back to Define Services, confirm your target service boundaries, and
            click <strong>Finalize Decomposition</strong> before proceeding here.
          </p>
          {discoverError && (
            <div className="gsv-warn-banner" style={{ maxWidth: 480 }}>
              <AlertTriangle size={13} />
              <span>{discoverError}</span>
            </div>
          )}
          <button className="gsv-btn gsv-btn-primary" onClick={() => onGoToDecomposeCode?.()}>
            <ArrowLeft size={13} />Go to Define Services
          </button>
        </div>
      )
    }

    // ── READY STATE ────────────────────────────────────────────────────────
    return (
      <div className="gsv-ready">

        {/* Step description */}
        <p className="gsv-step-desc">
          Build Services generates Spring Boot microservice scaffolding from your approved
          decomposition plan. Review the services below, then run.
        </p>

        {/* Services to generate — pre-flight summary */}
        <div className="gsv-contract-card">
          <div className="gsv-contract-card-hdr">
            <Layers size={14} />
            <span>Services to Generate</span>
            {contractSummary.isApproved && (
              <span className="gsv-contract-badge gsv-contract-badge-approved">Contract approved</span>
            )}
          </div>
          <div className="gsv-contract-body">
            <div className="gsv-contract-stat">
              <span className="gsv-stat-num">{contractSummary.serviceCount}</span>
              <span className="gsv-stat-lbl">
                Target Service{contractSummary.serviceCount !== 1 ? 's' : ''} from approved plan
              </span>
            </div>

            {contractSummary.services.length > 0 && (
              <div className="gsv-preflight-table">
                {contractSummary.services.map((svc, i) => {
                  const memberCount = svc.members?.length || svc.classes || 0
                  return (
                    <div key={svc.id || i} className="gsv-preflight-svc-row">
                      <div className="gsv-preflight-svc-left">
                        <FileCode size={12} className="gsv-preflight-svc-ico" />
                        <span className="gsv-preflight-svc-name">{svc.name}</span>
                        {svc.domain && svc.domain !== svc.name && (
                          <span className="gsv-preflight-svc-domain">{svc.domain}</span>
                        )}
                      </div>
                      <div className="gsv-preflight-svc-right">
                        {memberCount > 0 && (
                          <span className="gsv-preflight-svc-count">
                            {memberCount} class{memberCount !== 1 ? 'es' : ''}
                          </span>
                        )}
                        {(svc.loc || 0) > 0 && (
                          <span className="gsv-preflight-svc-loc">
                            {svc.loc >= 1000 ? `${(svc.loc / 1000).toFixed(1)}k` : svc.loc}L
                          </span>
                        )}
                        {svc.cohesion && (
                          <span className={`gsv-preflight-svc-cohesion ${svc.cohesion}`}>
                            {svc.cohesion}
                          </span>
                        )}
                        <span className={`gsv-preflight-svc-status ${svc.status || 'approved'}`}>
                          {svc.status === 'approved' ? '✓ Confirmed' : svc.status || 'confirmed'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Error banners */}
        {runError && (
          <div className="gsv-error-banner">
            <AlertCircle size={14} />
            <span>{runError}</span>
          </div>
        )}
        {discoverError && (
          <div className="gsv-warn-banner">
            <AlertTriangle size={14} />
            <span>Warning: {discoverError} — you can still attempt to run.</span>
          </div>
        )}

        {/* Progress bar */}
        {running && (
          <div className="gsv-progress">
            <div className="gsv-progress-label">
              <Loader2 size={13} className="gsv-spin" />
              <span>{progressMsg || 'Running…'}</span>
              <span className="gsv-progress-pct">{Math.round(progress)}%</span>
            </div>
            <div className="gsv-progress-track">
              <div className="gsv-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Action row */}
        <div className="gsv-run-row">
          <button
            className={`gsv-btn gsv-btn-primary${running ? ' gsv-btn-running' : ''}`}
            onClick={handleRun}
            disabled={running}
          >
            {running
              ? <><Loader2 size={14} className="gsv-spin" />Running…</>
              : <><Zap size={14} />Run Build Services</>
            }
          </button>
          <button
            className="gsv-btn gsv-btn-secondary"
            onClick={() => onGoToDecomposeCode?.()}
            disabled={running}
          >
            <ArrowLeft size={13} />Back to Define Services
          </button>
        </div>

      </div>
    )
  }

  return (
    <div className="gsv-root">
      {renderNavHeader()}
      {renderStageHeader()}
      <div className="gsv-body">
        {renderContent()}
      </div>
    </div>
  )
}
